import React, { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { crushshyAdminApi } from '../../../services/api';
import { useToast } from '../../../hooks/use-toast';
import { useCrushShyT } from '../../../hooks/useCrushShyT';
import CrushShyPageHeader from './CrushShyPageHeader';
import { crushCard, crushInput, crushBtnPrimary } from './crushshyTheme';

const CrushShySettings = () => {
  const [settings, setSettings] = useState(null);
  const { toast } = useToast();
  const t = useCrushShyT();

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    try {
      const res = await crushshyAdminApi.getSettings();
      setSettings(res.data.settings);
    } catch {
      toast({ title: t('toastLoadSettingsError'), variant: 'destructive' });
    }
  };

  const save = async () => {
    try {
      await crushshyAdminApi.updateSettings(settings);
      toast({ title: t('toastSettingsSaved') });
    } catch {
      toast({ title: t('toastSaveError'), variant: 'destructive' });
    }
  };

  if (!settings) {
    return (
      <div className="p-8 flex justify-center">
        <div className="w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const flags = settings.featureFlags || {};

  return (
    <div className="p-6 sm:p-8 max-w-3xl">
      <CrushShyPageHeader title={t('settingsTitle')} subtitle={t('settingsSubtitle')} />

      <div className={`${crushCard} p-6 space-y-4 mb-6`}>
        <h2 className="text-lg font-semibold text-white">{t('settingsPremium')}</h2>
        <Toggle
          label={t('settingsPremiumFreeForAll')}
          hint={t('settingsPremiumFreeForAllHint')}
          checked={settings.premiumFreeForAll !== false}
          onChange={(v) => setSettings({ ...settings, premiumFreeForAll: v })}
        />
      </div>

      <div className={`${crushCard} p-6 space-y-4 mb-6`}>
        <h2 className="text-lg font-semibold text-white">{t('settingsMobileApp')}</h2>
        <Toggle
          label={t('settingsDemoUsers')}
          hint={t('settingsDemoUsersHint')}
          checked={settings.showDemoUsers !== false}
          onChange={(v) => setSettings({ ...settings, showDemoUsers: v })}
        />
      </div>

      <div className={`${crushCard} p-6 space-y-6 mb-6`}>
        <h2 className="text-lg font-semibold text-white">{t('settingsAvailability')}</h2>
        <Toggle
          label={t('settingsMaintenance')}
          hint={t('settingsMaintenanceHint')}
          checked={!!settings.maintenanceMode}
          onChange={(v) => setSettings({ ...settings, maintenanceMode: v })}
        />
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('settingsMaintenanceMessage')}</label>
          <textarea
            value={settings.maintenanceMessage || ''}
            onChange={(e) => setSettings({ ...settings, maintenanceMessage: e.target.value })}
            rows={2}
            className={crushInput}
          />
        </div>
      </div>

      <div className={`${crushCard} p-6 space-y-4 mb-6`}>
        <h2 className="text-lg font-semibold text-white">{t('settingsRules')}</h2>
        <NumberField
          label={t('settingsMinAge')}
          value={settings.minAge}
          onChange={(v) => setSettings({ ...settings, minAge: v })}
        />
        <NumberField
          label={t('settingsMaxDistance')}
          value={settings.maxDistanceMetersDefault}
          onChange={(v) => setSettings({ ...settings, maxDistanceMetersDefault: v })}
        />
        <NumberField
          label={t('settingsAutoSuspend')}
          value={settings.moderationAutoSuspendReports}
          onChange={(v) => setSettings({ ...settings, moderationAutoSuspendReports: v })}
        />
        <Toggle
          label="Forcer la mise à jour (ON)"
          hint="Désactivé par défaut. Si activé, les versions inférieures à la version minimale sont bloquées au lancement."
          checked={settings.forceUpdateEnabled === true}
          onChange={(v) => setSettings({ ...settings, forceUpdateEnabled: v })}
        />
        <Field
          label={t('settingsMinAppVersion')}
          value={settings.minAppVersion}
          onChange={(v) => setSettings({ ...settings, minAppVersion: v })}
        />
        <Toggle
          label={t('settingsRequirePhoto')}
          checked={!!settings.requireProfilePhoto}
          onChange={(v) => setSettings({ ...settings, requireProfilePhoto: v })}
        />
      </div>

      <div className={`${crushCard} p-6 space-y-4 mb-6`}>
        <h2 className="text-lg font-semibold text-white">{t('settingsCoPresence')}</h2>
        <p className="text-xs text-gray-500">{t('settingsCoPresenceHint')}</p>
        <Toggle
          label={t('settingsCoPresenceEnabled')}
          checked={settings.coPresenceEnabled !== false}
          onChange={(v) => setSettings({ ...settings, coPresenceEnabled: v })}
        />
        <NumberField
          label={t('settingsCoPresenceRadius')}
          value={settings.coPresenceRadiusMeters ?? 20}
          onChange={(v) => setSettings({ ...settings, coPresenceRadiusMeters: v })}
        />
        <NumberField
          label={t('settingsCoPresenceDays')}
          value={settings.coPresenceMinOccurrencesPerMonth ?? 5}
          onChange={(v) => setSettings({ ...settings, coPresenceMinOccurrencesPerMonth: v })}
        />
      </div>

      <div className={`${crushCard} p-6 space-y-4 mb-8`}>
        <h2 className="text-lg font-semibold text-white">{t('settingsFeatures')}</h2>
        <Toggle
          label={t('settingsLiveMap')}
          checked={!!flags.liveMap}
          onChange={(v) =>
            setSettings({
              ...settings,
              featureFlags: { ...flags, liveMap: v },
            })
          }
        />
        <Toggle
          label={t('settingsChat')}
          checked={!!flags.chat}
          onChange={(v) =>
            setSettings({
              ...settings,
              featureFlags: { ...flags, chat: v },
            })
          }
        />
        <Toggle
          label={t('settingsCrushActions')}
          checked={!!flags.crushActions}
          onChange={(v) =>
            setSettings({
              ...settings,
              featureFlags: { ...flags, crushActions: v },
            })
          }
        />
        <Toggle
          label={t('settingsCoPresenceApp')}
          checked={flags.recurringCoPresence !== false}
          onChange={(v) =>
            setSettings({
              ...settings,
              featureFlags: { ...flags, recurringCoPresence: v },
            })
          }
        />
      </div>

      <button type="button" onClick={save} className={`${crushBtnPrimary} flex items-center gap-2`}>
        <Save className="w-5 h-5" />
        {t('settingsSave')}
      </button>
    </div>
  );
};

const Toggle = ({ label, hint, checked, onChange }) => (
  <label className="flex items-start gap-3 cursor-pointer">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-1 rounded border-pink-500/40"
    />
    <span>
      <span className="text-sm text-white font-medium">{label}</span>
      {hint && <span className="block text-xs text-gray-500 mt-0.5">{hint}</span>}
    </span>
  </label>
);

const NumberField = ({ label, value, onChange }) => (
  <div>
    <label className="block text-xs text-gray-500 mb-1">{label}</label>
    <input
      type="number"
      value={value ?? 0}
      onChange={(e) => onChange(Number(e.target.value))}
      className={crushInput}
    />
  </div>
);

const Field = ({ label, value, onChange }) => (
  <div>
    <label className="block text-xs text-gray-500 mb-1">{label}</label>
    <input type="text" value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={crushInput} />
  </div>
);

export default CrushShySettings;
